import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Download, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Package,
  Package2,
  ShoppingCart,
  RotateCcw,
  DollarSign,
  Wallet,
  BarChart3,
  Cookie,
  Coffee,
  Cake,
  FileSpreadsheet,
  Printer
} from 'lucide-react';

const Reports = () => {
  const { user, API } = useContext(UserContext);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(true);

  const counters = [
    { 
      id: 'pain', 
      name: 'Pains', 
      icon: () => <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-6 w-6 object-cover rounded" />, 
      color: 'from-yellow-400 to-orange-500' 
    },
    { 
      id: 'viennoiserie', 
      name: 'Viennoiserie', 
      icon: () => <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-6 w-6 object-cover rounded" />, 
      color: 'from-purple-400 to-pink-500' 
    },
    { id: 'gateaux', name: 'Gâteaux', icon: Cake, color: 'from-pink-400 to-rose-500' }
  ];

  useEffect(() => {
    fetchReports();
  }, [selectedDate]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      // Fetch detailed reports
      const reportsResponse = await axios.get(`${API}/dashboard/daily-report`, {
        params: { date: selectedDate }
      });
      setReports(reportsResponse.data);
      
      // Fetch summary
      const summaryResponse = await axios.get(`${API}/dashboard/summary`, {
        params: { date: selectedDate }
      });
      setSummary(summaryResponse.data);
      
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString()} F CFA`;
  };

  const getCounterDisplayName = (counter) => {
    const counterNames = {
      'pain': 'Pains',
      'viennoiserie': 'Viennoiserie', 
      'gateaux': 'Gâteaux'
    };
    return counterNames[counter] || counter;
  };

  const getCounterData = (counterId) => {
    return summary?.counters.find(c => c.counter === counterId) || {
      total_produced: 0,
      total_sold: 0,
      total_returned: 0,
      total_remaining: 0,
      revenue: 0
    };
  };

  const calculateProductionRate = (produced, sold, returned) => {
    if (produced === 0) return 0;
    const usedProducts = sold + returned;
    return Math.round((usedProducts / produced) * 100);
  };

  const calculateWasteRate = (produced, returned) => {
    if (produced === 0) return 0;
    return Math.round((returned / produced) * 100);
  };

  const exportToExcel = () => {
    try {
      if (!reports || reports.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }

      // Format Excel avec totaux et calculs
      const excelData = [
        // En-têtes avec style
        ['RAPPORT DÉTAILLÉ DE PRODUCTION'],
        [`Date: ${new Date(selectedDate).toLocaleDateString('fr-FR')}`],
        [`Généré le: ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`],
        [`Par: ${user?.username || 'Utilisateur'} (${user?.role || 'N/A'})`],
        [''],
        
        // Résumé financier
        ['=== RÉSUMÉ FINANCIER ==='],
        ['Chiffre d\'Affaires Total', formatCurrency(summary?.total_revenue || 0)],
        ['Coût Matières Premières', formatCurrency(summary?.total_supply_costs || 0)],
        ['Dépenses Quotidiennes', formatCurrency(summary?.total_expenses || 0)],
        ['Total Coûts', formatCurrency(summary?.total_costs || 0)],
        ['Bénéfice Net', formatCurrency(summary?.net_result || 0)],
        [''],
        
        // En-têtes du tableau détaillé
        [
          'Produit',
          'Guichet', 
          'Prix Unitaire',
          'Quantité Produite',
          'Quantité Vendue',
          'Quantité Retournée',
          'Quantité Restante',
          'Coût Matières Premières',
          'Dépenses Quotidiennes',
          'Total Coût',
          'Chiffre d\'Affaires',
          'Bénéfice Net',
          'Taux Utilisation (%)'
        ],
        
        // Données détaillées
        ...reports.map(report => [
          report.product_name,
          report.counter === 'pain' ? 'Pains' : 
          report.counter === 'viennoiserie' ? 'Viennoiserie' : 'Gâteaux',
          report.price,
          report.total_produced,
          report.total_sold,
          report.total_returned,
          report.remaining,
          Math.round(report.supply_cost || 0),
          Math.round(report.daily_expenses || 0),
          Math.round(report.total_cost || 0),
          report.revenue,
          Math.round(report.revenue - (report.total_cost || 0)),
          report.total_produced > 0 ? 
            Math.round(((report.total_sold + report.total_returned) / report.total_produced) * 100) : 0
        ])
      ];

      // Convertir en CSV pour Excel
      const csvString = excelData.map(row => 
        row.map(cell => 
          typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
            ? `"${cell.replace(/"/g, '""')}"` 
            : cell
        ).join(',')
      ).join('\n');

      const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-detaille-excel-${selectedDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Rapport Excel téléchargé avec succès !');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const exportCompleteReport = async () => {
    try {
      toast.info('Génération du rapport complet...');
      
      const response = await axios.get(`${API}/reports/detailed-export?date=${selectedDate}&user_role=${user.role}`);
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-complet-${selectedDate}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Rapport complet JSON téléchargé !');
    } catch (error) {
      console.error('Error exporting complete report:', error);
      toast.error('Erreur lors de l\'export complet');
    }
  };

  const printReport = () => {
    window.print();
  };

  const exportToCSV = () => {
    try {
      if (!reports || reports.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }

      // Préparer les données CSV avec toutes les colonnes du rapport détaillé
      const csvData = reports.map(report => ({
        'Produit': report.product_name,
        'Guichet': report.counter === 'pain' ? 'Pains' : 
                   report.counter === 'viennoiserie' ? 'Viennoiserie' : 
                   'Gâteaux',
        'Prix Unitaire (F CFA)': report.price,
        'Quantité Produite': report.total_produced,
        'Quantité Vendue': report.total_sold,
        'Quantité Retournée': report.total_returned,
        'Quantité Restante': report.remaining,
        'Coût Matières Premières (F CFA)': Math.round(report.supply_cost || 0),
        'Dépenses Quotidiennes (F CFA)': Math.round(report.daily_expenses || 0),
        'Total Coût (F CFA)': Math.round(report.total_cost || 0),
        'Chiffre d\'Affaires (F CFA)': report.revenue,
        'Bénéfice Net (F CFA)': Math.round(report.revenue - (report.total_cost || 0)),
        'Taux Utilisation (%)': report.total_produced > 0 ? 
          Math.round(((report.total_sold + report.total_returned) / report.total_produced) * 100) : 0
      }));

      // Ajouter un résumé en haut
      const summaryData = [
        {
          'Produit': '=== RÉSUMÉ DU JOUR ===',
          'Guichet': selectedDate,
          'Prix Unitaire (F CFA)': '',
          'Quantité Produite': reports.reduce((sum, r) => sum + r.total_produced, 0),
          'Quantité Vendue': reports.reduce((sum, r) => sum + r.total_sold, 0),
          'Quantité Retournée': reports.reduce((sum, r) => sum + r.total_returned, 0),
          'Quantité Restante': reports.reduce((sum, r) => sum + r.remaining, 0),
          'Coût Matières Premières (F CFA)': Math.round(reports.reduce((sum, r) => sum + (r.supply_cost || 0), 0)),
          'Dépenses Quotidiennes (F CFA)': Math.round(summary?.total_expenses || 0),
          'Total Coût (F CFA)': Math.round(reports.reduce((sum, r) => sum + (r.total_cost || 0), 0)),
          'Chiffre d\'Affaires (F CFA)': reports.reduce((sum, r) => sum + r.revenue, 0),
          'Bénéfice Net (F CFA)': Math.round(summary?.net_result || 0),
          'Taux Utilisation (%)': ''
        },
        {}, // Ligne vide
        ...csvData
      ];

      const csvString = [
        Object.keys(csvData[0]).join(','),
        ...summaryData.map(row => 
          Object.values(row).map(val => 
            typeof val === 'string' && val.includes(',') ? `"${val}"` : val
          ).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-detaille-${selectedDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Rapport CSV téléchargé avec succès !');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  const exportToPDF = () => {
    try {
      // Créer un document HTML pour impression PDF
      const printWindow = window.open('', '_blank');
      const currentDate = new Date().toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Rapport Détaillé - ${selectedDate}</title>
          <meta charset="UTF-8">
          <style>
            body { font-family: 'Inter', Arial, sans-serif; margin: 20px; color: #1f2937; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; }
            .company-info { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 30px; }
            .metric-card { background: #fef3c7; padding: 15px; border-radius: 8px; text-align: center; }
            .metric-value { font-size: 24px; font-weight: bold; color: #1f2937; }
            .metric-label { font-size: 12px; color: #6b7280; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 12px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
            th { background-color: #f9fafb; font-weight: 600; color: #374151; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .positive { color: #059669; font-weight: 600; }
            .negative { color: #dc2626; font-weight: 600; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 15px; }
            @media print { body { margin: 0; } .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 Rapport Détaillé de Production</h1>
            <h2>Boulangerie - ${currentDate}</h2>
            <p><strong>Date du rapport :</strong> ${new Date(selectedDate).toLocaleDateString('fr-FR')}</p>
          </div>
          
          <div class="company-info">
            <p><strong>🏢 Entreprise :</strong> Système de Suivi de Production Boulangerie</p>
            <p><strong>👤 Créé par :</strong> Elouho Ettien Ane Martial T. - Consultant Juriste-Fiscaliste & Financier</p>
            <p><strong>📞 Contact :</strong> +225 07 69 54 54 69</p>
            <p><strong>🕒 Généré le :</strong> ${currentDate} par ${user?.username || 'Utilisateur'}</p>
          </div>

          <div class="metrics">
            <div class="metric-card">
              <div class="metric-value positive">${summary ? formatCurrency(summary.total_revenue) : '0 F CFA'}</div>
              <div class="metric-label">💰 Chiffre d'Affaires Total</div>
            </div>
            <div class="metric-card">
              <div class="metric-value negative">${summary ? formatCurrency(summary.total_costs || 0) : '0 F CFA'}</div>
              <div class="metric-label">💸 Total des Coûts</div>
            </div>
            <div class="metric-card">
              <div class="metric-value ${summary && summary.net_result >= 0 ? 'positive' : 'negative'}">
                ${summary ? formatCurrency(summary.net_result || 0) : '0 F CFA'}
              </div>
              <div class="metric-label">📈 Bénéfice Net</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Guichet</th>
                <th class="text-right">Prix Unit.</th>
                <th class="text-center">Produit</th>
                <th class="text-center">Vendu</th>
                <th class="text-center">Retourné</th>
                <th class="text-center">Restant</th>
                <th class="text-right">Coût Matières</th>
                <th class="text-right">Dép. Quotid.</th>
                <th class="text-right">Total Coût</th>
                <th class="text-right">CA</th>
                <th class="text-right">Bénéfice Net</th>
              </tr>
            </thead>
            <tbody>
              ${reports.map(report => `
                <tr>
                  <td><strong>${report.product_name}</strong></td>
                  <td>${report.counter === 'pain' ? 'Pains' : report.counter === 'viennoiserie' ? 'Viennoiserie' : 'Gâteaux'}</td>
                  <td class="text-right">${formatCurrency(report.price)}</td>
                  <td class="text-center">${report.total_produced}</td>
                  <td class="text-center">${report.total_sold}</td>
                  <td class="text-center">${report.total_returned}</td>
                  <td class="text-center">${report.remaining}</td>
                  <td class="text-right">${formatCurrency(report.supply_cost || 0)}</td>
                  <td class="text-right">${formatCurrency(report.daily_expenses || 0)}</td>
                  <td class="text-right">${formatCurrency(report.total_cost || 0)}</td>
                  <td class="text-right">${formatCurrency(report.revenue)}</td>
                  <td class="text-right ${(report.revenue - (report.total_cost || 0)) >= 0 ? 'positive' : 'negative'}">
                    ${formatCurrency(report.revenue - (report.total_cost || 0))}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p><strong>Document généré automatiquement le ${currentDate}</strong></p>
            <p>© 2025 Système de Suivi de Production Boulangerie - Créé par Elouho Ettien Ane Martial T.</p>
            <p>Consultant Juriste-Fiscaliste & Financier • Tél: +225 07 69 54 54 69</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
              }, 1000);
            };
          </script>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      toast.success('Rapport PDF en cours de génération...');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Génération du rapport...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="content-wrapper px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="max-width-container flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 fade-in">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Rapports de Production
            </h1>
            <p className="text-gray-600">
              Analyse détaillée des performances par guichet
            </p>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="report-date-picker"
            />
            <Button
              onClick={fetchReports}
              variant="outline"
              size="sm"
              className="btn-hover"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button
              onClick={printReport}
              variant="outline"
              size="sm"
              className="no-print btn-hover"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimer
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              className="no-print btn-hover bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              CSV Simple
            </Button>
            <Button
              onClick={exportToPDF}
              variant="outline"
              size="sm"
              className="no-print btn-hover bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
            >
              <FileText className="h-4 w-4 mr-2" />
              PDF Détaillé
            </Button>
            <Button
              onClick={exportToExcel}
              variant="outline"
              size="sm"
              className="no-print btn-hover bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel Complet
            </Button>
            <Button
              onClick={exportCompleteReport}
              variant="outline"
              size="sm"
              className="no-print btn-hover bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
            >
              <Download className="h-4 w-4 mr-2" />
              Rapport Complet
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="dashboard-metrics grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="print-friendly bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Chiffre d'Affaires Total
                  </p>
                  <p className="text-2xl font-bold text-green-600" data-testid="total-revenue-report">
                    {summary ? formatCurrency(summary.total_revenue) : '0 F CFA'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="print-friendly bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Coût Matières Premières
                  </p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="total-supply-costs-report">
                    {summary ? formatCurrency(summary.total_supply_costs || 0) : '0 F CFA'}
                  </p>
                </div>
                <Package2 className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="print-friendly bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Autres Dépenses
                  </p>
                  <p className="text-2xl font-bold text-red-600" data-testid="total-expenses-report">
                    {summary ? formatCurrency(summary.total_expenses || 0) : '0 F CFA'}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="print-friendly bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Coûts
                  </p>
                  <p className="text-2xl font-bold text-purple-600" data-testid="total-costs-report">
                    {summary ? formatCurrency(summary.total_costs || 0) : '0 F CFA'}
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="print-friendly bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Bénéfice Net Total
                  </p>
                  <p className={`text-2xl font-bold ${summary && summary.net_result >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="net-result-report">
                    {summary ? formatCurrency(summary.net_result || 0) : '0 F CFA'}
                  </p>
                </div>
                {summary && summary.net_result >= 0 ? 
                  <TrendingUp className="h-8 w-8 text-green-600" /> :
                  <TrendingDown className="h-8 w-8 text-red-600" />
                }
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Counter Performance */}
        <div className="max-width-cards grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {counters.map((counter, index) => {
            const data = getCounterData(counter.id);
            const Icon = counter.icon;
            const productionRate = calculateProductionRate(data.total_produced, data.total_sold, data.total_returned);
            const wasteRate = calculateWasteRate(data.total_produced, data.total_returned);
            
            return (
              <Card key={counter.id} className={`print-friendly bg-white/70 glass border-0 shadow-lg slide-up`} style={{animationDelay: `${index * 0.1}s`}}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className={`h-10 w-10 bg-gradient-to-r ${counter.color} rounded-lg flex items-center justify-center text-white`}>
                      {counter.id === 'pain' ? 
                        <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-6 w-6 object-cover rounded" /> : 
                        counter.id === 'viennoiserie' ?
                        <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-6 w-6 object-cover rounded" /> :
                        <Icon className="h-6 w-6" />
                      }
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{getCounterDisplayName(counter.id)}</h3>
                      <p className="text-sm text-gray-600">Performance du guichet</p>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{data.total_produced}</p>
                      <p className="text-xs text-gray-600">Produits</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{data.total_sold}</p>
                      <p className="text-xs text-gray-600">Vendus</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{data.total_returned}</p>
                      <p className="text-xs text-gray-600">Retours</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">{data.total_remaining}</p>
                      <p className="text-xs text-gray-600">Restants</p>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Chiffre d'affaires</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(data.revenue)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Taux d'utilisation</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{productionRate}%</span>
                        {productionRate >= 80 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Taux de perte</span>
                      <Badge variant={wasteRate > 10 ? "destructive" : "secondary"}>
                        {wasteRate}%
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detailed Report Table */}
        <Card className="max-width-container print-friendly bg-white/70 glass border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Rapport Détaillé - {new Date(selectedDate).toLocaleDateString('fr-FR')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table w-full" data-testid="detailed-report-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Guichet</th>
                    <th>Prix Unitaire</th>
                    <th>Produit</th>
                    <th>Vendu</th>
                    <th>Retourné</th>
                    <th>Restant</th>
                    <th>Coût Matières Premières</th>
                    <th>Dépenses Quotidiennes</th>
                    <th>Total Coût</th>
                    <th>Chiffre d'Affaires</th>
                    <th>Bénéfice Net</th>
                    <th className="no-print">Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report, index) => {
                    const utilizationRate = report.total_produced > 0 
                      ? Math.round(((report.total_sold + report.total_returned) / report.total_produced) * 100)
                      : 0;
                    
                    return (
                      <tr key={report.product_id || index}>
                        <td className="font-medium">{report.product_name}</td>
                        <td>
                          <Badge className={`counter-${report.counter}`}>
                            {getCounterDisplayName(report.counter)}
                          </Badge>
                        </td>
                        <td>{formatCurrency(report.price)}</td>
                        <td className="text-center">
                          <span className="font-semibold text-blue-600">
                            {report.total_produced}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="font-semibold text-green-600">
                            {report.total_sold}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="font-semibold text-red-600">
                            {report.total_returned}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className={`font-semibold ${
                            report.remaining > 0 ? 'text-orange-600' : 'text-gray-400'
                          }`}>
                            {report.remaining}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className="font-semibold text-orange-600">
                            {formatCurrency(report.supply_cost || 0)}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className="font-semibold text-red-600">
                            {formatCurrency(report.daily_expenses || 0)}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className="font-semibold text-purple-600">
                            {formatCurrency(report.total_cost || 0)}
                          </span>
                        </td>
                        <td className="text-right font-semibold text-green-600">
                          {formatCurrency(report.revenue)}
                        </td>
                        <td className="text-right font-semibold">
                          <span className={`${
                            (report.revenue - (report.total_cost || 0)) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(report.revenue - (report.total_cost || 0))}
                          </span>
                        </td>
                        <td className="text-center no-print">
                          <Badge 
                            variant={utilizationRate >= 80 ? "default" : utilizationRate >= 50 ? "secondary" : "destructive"}
                          >
                            {utilizationRate}%
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {reports.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune donnée disponible
                </h3>
                <p className="text-gray-600">
                  Aucune production n'a été enregistrée pour cette date.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default Reports;